import React, { PropsWithChildren, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Patient } from '@models';
import { AlertService } from '../../../core/services/alert.service';
import { MembershipInformation } from '../../../core/types/memberships';
import StatusChip, { ChipStatus } from './StatusChip';
import ProductAccordionGroup from './ProductAccordionGroup';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import { formatAsDecimal } from '../../utils';
import { environment } from '../../../../environments/environment';

export type PatientInfoMembershipProps = PropsWithChildren<{
  alertService: AlertService;
  patient: Patient;
  membership: MembershipInformation;
}>;

export function PatientInfoMembership({
  alertService,
  patient,
  membership,
}: PatientInfoMembershipProps): React.JSX.Element {
  const visitFee = useMemo(() => {
    if (membership?.visitFeeInCents && membership.visitFeeInCents > 0) {
      return '$' + formatAsDecimal(membership.visitFeeInCents / 100);
    } else if (membership?.visitFeeInCents && membership.visitFeeInCents === 0) {
      return '$' + formatAsDecimal(0);
    }

    return 'N/A';
  }, [membership.visitFeeInCents]);

  const membershipStatus: ChipStatus = useMemo(() => {
    if (membership?.statusReason === 'INELIGIBLE') {
      return 'INELIGIBLE';
    } else if (membership) {
      return membership.status;
    }

    return 'UNVERIFIED';
  }, [membership]);

  return (
    <>
      {membership.membershipSource === 'CORE_PRODUCT' && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: '17px',
              marginBottom: '12px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MedicalServicesOutlinedIcon sx={{ fontSize: '18px' }} />
              <Typography variant={'subtitle2'} sx={{ marginLeft: '5px' }}>
                Nice Care
              </Typography>
            </Box>
            <Box>
              {membership.status && environment.featuresEnabled.showMembershipStatus && (
                <StatusChip status={membershipStatus} />
              )}
            </Box>
          </Box>
          {membership.company && (
            <Box sx={{ marginTop: '5px' }}>
              <Typography component={'span'} variant={'subtitle2'}>
                Employer:{' '}
              </Typography>
              <Typography component={'span'} variant={'body2'}>
                {membership.company.name}
              </Typography>
            </Box>
          )}
          <Box sx={{ marginTop: '2px' }}>
            <Typography component={'span'} variant={'subtitle2'}>
              Visit fee:{' '}
            </Typography>
            <Typography component={'span'} variant={'body2'}>
              {visitFee}
            </Typography>
          </Box>
        </>
      )}
      {membership.membershipSource === 'HEALTH_PLAN' && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '17px', marginBottom: '12px' }}>
            <HealthAndSafetyOutlinedIcon sx={{ fontSize: '18px' }} />
            <Typography variant={'subtitle2'} sx={{ marginLeft: '5px' }}>
              Nice Health Plan
            </Typography>
          </Box>
          {membership.company && (
            <Box sx={{ marginTop: '5px' }}>
              <Typography component={'span'} variant={'subtitle2'}>
                Employer:{' '}
              </Typography>
              <Typography component={'span'} variant={'body2'}>
                {membership.company.name}
              </Typography>
            </Box>
          )}
        </>
      )}
      {membership.products && <ProductAccordionGroup products={membership.products} />}
    </>
  );
}

export default PatientInfoMembership;
